import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/Card';
import apiClient from '../../utils/api';

// Suggestions drawn from patterns confirmed in your own data (N8).
//
// The whole point of the surface is that it reads as a menu. There is no "next",
// no progress bar and no count of what's outstanding: a suggestion nobody picked
// up is not unfinished work. Declining is a first-class button sitting right
// next to accepting, at the same weight — a "no" that costs more taps than a
// "yes" is a mandate wearing a menu's clothes.
//
// Every card carries its own caveat rather than relying on one disclaimer at the
// top of the page, because a suggestion that gets screenshotted or read aloud
// should travel with the thing that qualifies it.

const FLAG_STYLE = {
  supported: 'bg-primary-500/15 text-primary-700 dark:text-primary-300',
  test: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
};
const FLAG_LABEL = { supported: 'We predicted this one', test: 'Worth a try' };

const READ_LABEL = {
  pending: 'Still running',
  void_coverage: "Couldn't read it",
  not_taken: 'Not taken up',
  done: 'Read back',
};

const Suggestion = ({ item, onAccept, onDecline, busy }) => (
  <div className="rounded-lg border border-surface-border dark:border-surface-dark-border p-4">
    <div className="flex items-start justify-between gap-3">
      <p className="text-base font-semibold text-gray-900 dark:text-white">
        {item.headline}
      </p>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-sm font-medium ${FLAG_STYLE[item.flag] || FLAG_STYLE.test}`}>
        {FLAG_LABEL[item.flag] || FLAG_LABEL.test}
      </span>
    </div>
    {item.rationale && (
      <p className="mt-2 text-base text-gray-700 dark:text-gray-200 leading-relaxed">
        {item.rationale}
      </p>
    )}
    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
      {item.flag_note}
    </p>
    {item.status === 'offered' ? (
      // Same size, same row, same weight. Saying no is not the small grey
      // option under the real one.
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => onAccept(item.id)}
          className="flex-1 rounded-lg bg-primary-500 px-3 py-2 text-base font-semibold text-[#1a1200] disabled:opacity-50"
        >
          I'll try it
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onDecline(item.id)}
          className="flex-1 rounded-lg border border-surface-border dark:border-surface-dark-border px-3 py-2 text-base font-semibold text-gray-800 dark:text-gray-100 disabled:opacity-50"
        >
          Not for me
        </button>
      </div>
    ) : (
      <p className="mt-3 text-sm font-medium text-gray-600 dark:text-gray-300">
        Running for {item.horizon_days} days — we'll look back after that.
      </p>
    )}
  </div>
);

const Closed = ({ item }) => (
  <div className="rounded-lg border border-surface-border dark:border-surface-dark-border p-4">
    <div className="flex items-start justify-between gap-3">
      <p className="text-base font-semibold text-gray-900 dark:text-white">
        {item.headline}
      </p>
      <span className="shrink-0 text-sm font-medium text-gray-600 dark:text-gray-300">
        {READ_LABEL[item.read_state] || ''}
      </span>
    </div>
    {item.read?.note && (
      <p className="mt-2 text-base text-gray-700 dark:text-gray-200 leading-relaxed">
        {item.read.note}
      </p>
    )}
  </div>
);

const AdviceCard = () => {
  const [menu, setMenu] = useState(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/advice');
      setMenu(res.data);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
      setError(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const answer = async (id, accept) => {
    setBusy(true);
    try {
      await apiClient.post(`/api/advice/${id}/${accept ? 'accept' : 'decline'}`);
      await load();
    } catch (err) {
      console.error('Failed to answer suggestion:', err);
    } finally {
      setBusy(false);
    }
  };

  if (error || !menu || !menu.enabled) return null;

  const { offered = [], accepted = [], closed = [] } = menu;
  // Nothing to show is a legitimate resting state for this tier, not an empty
  // state to apologise for. The engine having nothing confirmed to say is the
  // normal case and the card simply isn't there.
  if (!offered.length && !accepted.length && !closed.length) return null;

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Things you could try
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            {menu.disclaimer}
          </p>
        </div>

        {offered.map((item) => (
          <Suggestion key={item.id} item={item} busy={busy}
            onAccept={(id) => answer(id, true)}
            onDecline={(id) => answer(id, false)} />
        ))}
        {accepted.map((item) => (
          <Suggestion key={item.id} item={item} busy={busy}
            onAccept={() => {}} onDecline={() => {}} />
        ))}

        {closed.length > 0 && (
          <div className="space-y-3 pt-2">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              What happened last time
            </h3>
            {closed.slice(0, 3).map((item) => <Closed key={item.id} item={item} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdviceCard;
